import { useState } from "react";

/* ── Types ── */
interface StagedVariable {
  id: string;
  variable_name: string;
  resolution: "data_match" | "proxy" | "correlation" | "synthetic";
  expert_name: string;
  source_column: string | null;   // matched/proxy column name
  prior_mean: number | null;
  prior_std: number | null;
  certainty: number;
  values: (number | string)[];    // preview of first N row values
  status: "staged" | "pushed" | "rejected";
  pushed_at: string | null;
}

/* ── Demo: existing dataset columns ── */
const EXISTING_COLUMNS = ["Study_Hours", "Household_Income", "Sleep_Quality", "Class_Attendance", "Prior_GPA", "Target"];

// Generate deterministic-ish demo values
function demoValues(resolution: string, seed: number): (number | string)[] {
  const rng = (s: number) => { let x = s; return () => { x = (x * 16807 + 13) % 2147483647; return (x % 1000) / 1000; }; };
  const r = rng(seed);
  if (resolution === "data_match") {
    return Array.from({ length: 15 }, () => +(r() * 4).toFixed(2));
  }
  if (resolution === "proxy") {
    return Array.from({ length: 15 }, () => +(r() * 0.8 + 0.1).toFixed(3));
  }
  if (resolution === "correlation") {
    return Array.from({ length: 15 }, () => +(r() * 2 - 0.5).toFixed(3));
  }
  // synthetic — ordinal 1/2/3
  return Array.from({ length: 15 }, () => Math.floor(r() * 3) + 1);
}

const DEMO_STAGED: StagedVariable[] = [
  {
    id: "stg_001", variable_name: "Income_Disparity_Index", resolution: "data_match",
    expert_name: "Thornton", source_column: "Household_Income",
    prior_mean: 0.35, prior_std: 0.18, certainty: 0.68,
    values: demoValues("data_match", 42), status: "staged", pushed_at: null,
  },
  {
    id: "stg_002", variable_name: "Geographic_Stability", resolution: "proxy",
    expert_name: "Paulson", source_column: "Class_Attendance",
    prior_mean: 0.30, prior_std: 0.20, certainty: 0.60,
    values: demoValues("proxy", 77), status: "staged", pushed_at: null,
  },
  {
    id: "stg_003", variable_name: "Domestic_Violence_Risk", resolution: "correlation",
    expert_name: "Washington", source_column: null,
    prior_mean: 0.65, prior_std: 0.08, certainty: 0.91,
    values: demoValues("correlation", 13), status: "staged", pushed_at: null,
  },
  {
    id: "stg_004", variable_name: "Parental_Involvement_Score", resolution: "synthetic",
    expert_name: "Hartwell", source_column: null,
    prior_mean: 0.40, prior_std: 0.12, certainty: 0.82,
    values: demoValues("synthetic", 99), status: "pushed", pushed_at: "2026-03-17 10:45",
  },
  {
    id: "stg_005", variable_name: "Child_Attachment_Quality", resolution: "synthetic",
    expert_name: "Kwan", source_column: null,
    prior_mean: 0.55, prior_std: 0.15, certainty: 0.74,
    values: demoValues("synthetic", 55), status: "staged", pushed_at: null,
  },
];

/* ── Existing dataset row values (demo) ── */
function existingRowValues(colIdx: number, rowIdx: number): string {
  const data: Record<string, number[]> = {
    Study_Hours: [4.2, 6.1, 3.8, 7.5, 5.0, 2.9, 8.3, 4.7, 6.8, 3.2, 5.5, 7.1, 4.0, 6.4, 5.8],
    Household_Income: [52000, 78000, 41000, 95000, 63000, 35000, 110000, 58000, 82000, 44000, 67000, 91000, 48000, 74000, 61000],
    Sleep_Quality: [0.72, 0.85, 0.61, 0.79, 0.68, 0.55, 0.91, 0.74, 0.82, 0.63, 0.77, 0.88, 0.66, 0.81, 0.70],
    Class_Attendance: [0.88, 0.95, 0.76, 0.92, 0.84, 0.71, 0.97, 0.86, 0.93, 0.78, 0.89, 0.94, 0.82, 0.91, 0.85],
    Prior_GPA: [3.2, 3.7, 2.8, 3.9, 3.4, 2.5, 3.8, 3.1, 3.6, 2.9, 3.5, 3.8, 3.0, 3.6, 3.3],
    Target: [1, 1, 0, 1, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 1],
  };
  const col = EXISTING_COLUMNS[colIdx];
  const vals = data[col];
  if (!vals || rowIdx >= vals.length) return "-";
  if (col === "Household_Income") return "$" + vals[rowIdx].toLocaleString();
  return String(vals[rowIdx]);
}

/* ── Config ── */
const RES_CONFIG: Record<string, { label: string; color: string; icon: string; desc: string }> = {
  data_match:  { label: "Data Match",  color: "#2563eb", icon: "DB", desc: "Pulled from existing column" },
  proxy:       { label: "Proxy",       color: "#7c3aed", icon: "PX", desc: "Derived from proxy column" },
  correlation: { label: "Correlation", color: "#0891b2", icon: "CR", desc: "Interaction term from correlates" },
  synthetic:   { label: "Synthetic",   color: "#059669", icon: "SN", desc: "Generated from expert prior" },
};

/* ── Main Component ── */
export default function VariableStaging() {
  const [variables, setVariables] = useState<StagedVariable[]>(DEMO_STAGED);
  const [selectedId, setSelectedId] = useState<string | null>("stg_001");

  const staged = variables.filter(v => v.status === "staged");
  const pushed = variables.filter(v => v.status === "pushed");
  const selected = variables.find(v => v.id === selectedId);

  const pushVariable = (id: string) => {
    setVariables(prev => prev.map(v =>
      v.id === id ? { ...v, status: "pushed" as const, pushed_at: new Date().toISOString().slice(0, 16).replace("T", " ") } : v
    ));
  };

  const removeVariable = (id: string) => {
    setVariables(prev => prev.map(v =>
      v.id === id ? { ...v, status: "rejected" as const } : v
    ));
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{
        padding: "20px 32px 14px",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        flexShrink: 0,
      }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          Variable Staging
          {staged.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}>
              {staged.length} ready to push
            </span>
          )}
          {pushed.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(16,185,129,0.1)", color: "#10b981" }}>
              {pushed.length} pushed
            </span>
          )}
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Preview resolved variables alongside your data — then push to model
        </p>
      </div>

      {/* Split layout: variable list left, data preview right */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left: variable list */}
        <div style={{
          width: 280, flexShrink: 0, borderRight: "1px solid rgba(0,0,0,0.06)",
          overflow: "auto", padding: "12px 16px",
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.3)", marginBottom: 8 }}>
            Resolved Variables
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {variables.filter(v => v.status !== "rejected").map(v => {
              const res = RES_CONFIG[v.resolution];
              const isSelected = selectedId === v.id;
              const isPushed = v.status === "pushed";

              return (
                <div
                  key={v.id}
                  onClick={() => setSelectedId(v.id)}
                  style={{
                    padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                    border: `1.5px solid ${isSelected ? res.color : "rgba(0,0,0,0.06)"}`,
                    background: isSelected ? res.color + "06" : "#fff",
                    transition: "all 0.15s ease",
                    opacity: isPushed ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 4, fontSize: 8, fontWeight: 800,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: res.color + "14", color: res.color,
                    }}>{res.icon}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.variable_name.replace(/_/g, " ")}
                    </span>
                    {isPushed && (
                      <svg width="14" height="14" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="9" fill="#10b981" />
                        <polyline points="6,10 9,13 14,7" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)", display: "flex", gap: 8 }}>
                    <span>{res.label}</span>
                    <span>{v.expert_name}</span>
                    {v.source_column && <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>← {v.source_column}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: data table preview */}
        <div style={{ flex: 1, overflow: "auto", padding: "12px 20px" }}>
          {selected ? (
            <>
              {/* Variable info bar */}
              <div style={{
                display: "flex", alignItems: "center", gap: 12, marginBottom: 14,
                padding: "10px 16px", borderRadius: 10,
                background: RES_CONFIG[selected.resolution].color + "08",
                border: `1px solid ${RES_CONFIG[selected.resolution].color}20`,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 6, fontSize: 10, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: RES_CONFIG[selected.resolution].color + "18",
                  color: RES_CONFIG[selected.resolution].color,
                }}>{RES_CONFIG[selected.resolution].icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{selected.variable_name}</div>
                  <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)" }}>
                    {RES_CONFIG[selected.resolution].desc}
                    {selected.source_column && <> from <strong>{selected.source_column}</strong></>}
                    {selected.prior_mean != null && !selected.source_column && (
                      <> · N({selected.prior_mean.toFixed(2)}, {selected.prior_std?.toFixed(2)})</>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {selected.status === "staged" && (
                    <>
                      <button
                        onClick={() => pushVariable(selected.id)}
                        style={{
                          padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
                          background: "#000", color: "#fff", fontSize: 11, fontWeight: 700,
                          display: "flex", alignItems: "center", gap: 6,
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" />
                        </svg>
                        Push to Model
                      </button>
                      <button
                        onClick={() => removeVariable(selected.id)}
                        style={{
                          padding: "8px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                          background: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.4)", fontSize: 11, fontWeight: 600,
                        }}
                      >
                        Remove
                      </button>
                    </>
                  )}
                  {selected.status === "pushed" && (
                    <span style={{
                      padding: "6px 14px", borderRadius: 8,
                      background: "rgba(16,185,129,0.1)", color: "#10b981",
                      fontSize: 11, fontWeight: 700,
                    }}>
                      Pushed {selected.pushed_at}
                    </span>
                  )}
                </div>
              </div>

              {/* Data table */}
              <div style={{
                borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}>
                {/* Header row */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: `40px ${EXISTING_COLUMNS.map(() => "1fr").join(" ")} 1fr`,
                  background: "rgba(0,0,0,0.04)",
                  fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                  color: "rgba(0,0,0,0.4)",
                }}>
                  <div style={{ padding: "8px 10px" }}>#</div>
                  {EXISTING_COLUMNS.map(col => (
                    <div key={col} style={{ padding: "8px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {col.replace(/_/g, " ")}
                    </div>
                  ))}
                  <div style={{
                    padding: "8px 6px",
                    background: RES_CONFIG[selected.resolution].color + "14",
                    color: RES_CONFIG[selected.resolution].color,
                    fontWeight: 800,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {selected.variable_name.replace(/_/g, " ")}
                    <span style={{ fontSize: 7, marginLeft: 4, opacity: 0.7 }}>NEW</span>
                  </div>
                </div>

                {/* Data rows */}
                {Array.from({ length: 15 }, (_, rowIdx) => (
                  <div
                    key={rowIdx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `40px ${EXISTING_COLUMNS.map(() => "1fr").join(" ")} 1fr`,
                      fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
                      borderTop: "1px solid rgba(0,0,0,0.04)",
                      background: rowIdx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                    }}
                  >
                    <div style={{ padding: "6px 10px", color: "rgba(0,0,0,0.25)", fontSize: 10 }}>{rowIdx + 1}</div>
                    {EXISTING_COLUMNS.map((col, colIdx) => (
                      <div key={col} style={{ padding: "6px 6px", color: "rgba(0,0,0,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {existingRowValues(colIdx, rowIdx)}
                      </div>
                    ))}
                    <div style={{
                      padding: "6px 6px",
                      background: RES_CONFIG[selected.resolution].color + "06",
                      color: RES_CONFIG[selected.resolution].color,
                      fontWeight: 600,
                    }}>
                      {selected.values[rowIdx] ?? "-"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer stats */}
              <div style={{
                display: "flex", gap: 12, marginTop: 12, fontSize: 10, color: "rgba(0,0,0,0.4)",
                padding: "8px 0",
              }}>
                <span>Showing 15 of 1,000 rows</span>
                <span>·</span>
                <span>Existing columns: {EXISTING_COLUMNS.length}</span>
                <span>·</span>
                <span style={{ color: RES_CONFIG[selected.resolution].color, fontWeight: 600 }}>
                  + {selected.variable_name} ({RES_CONFIG[selected.resolution].label})
                </span>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(0,0,0,0.3)", fontSize: 13 }}>
              Select a variable to preview
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
